// API configuration - PROD environment is default
// Note: This URL is used as a fallback for localStorage config
// The actual API URLs are managed per-company in the database (api_environment field)
// and resolved by edge functions using environment variables
const PROD_BASE_URL = import.meta.env.VITE_EXTERNAL_API_PROD_URL || "prod";

export const getApiBaseUrl = (): string => {
  const savedConfig = localStorage.getItem("api_config");
  if (savedConfig) {
    try {
      const config = JSON.parse(savedConfig);
      return config.baseUrl || PROD_BASE_URL;
    } catch {
      return PROD_BASE_URL;
    }
  }
  return PROD_BASE_URL;
};

// Helper to get auth headers for API calls
export const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("auth_token");
  const baseUrl = getApiBaseUrl();
  
  return {
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    'x-api-base-url': baseUrl,
    ...(token ? { 'x-app-token': token } : {}),
  };
};

export const getAuthToken = (): string | null => {
  return localStorage.getItem("auth_token");
};

// Handle API response - clears auth on 401 errors only for auth-system endpoints
export const handleApiResponse = async (response: Response): Promise<Response> => {
  // On 401 Unauthorized, check if it's from our auth system or external API
  if (response.status === 401) {
    try {
      const url = new URL(response.url);
      // Only clear token for auth-system errors (our authentication)
      // NOT for other edge functions (external API errors)
      const isAuthEndpoint = url.pathname.includes('/auth-system');
      
      if (isAuthEndpoint) {
        console.log('Auth: 401 from auth-system, clearing token');
        localStorage.removeItem('auth_token');
        window.dispatchEvent(new Event('auth-token-cleared'));
      } else {
        console.log('Auth: 401 from external API, NOT clearing token');
      }
    } catch {
      // If URL parsing fails, don't clear token to be safe
      console.log('Auth: Could not parse URL, not clearing token');
    }
  }
  return response;
};

// Wrapper for fetch with auth headers
export const authFetch = async (url: string, options?: RequestInit): Promise<Response> => {
  const response = await fetch(url, {
    ...options,
    credentials: 'include', // Required for httpOnly cookies
    headers: {
      ...getAuthHeaders(),
      ...options?.headers,
    },
  });
  return handleApiResponse(response);
};